import { HK_API_BASE_URL } from "@/config";
import type { AckSessionPayload } from "@/shared/utils/ackCart";

/**
 * Active Kids voucher endpoints on next-app.
 *
 * The routes are origin-locked and throttled per IP and per cart, so the widget must not
 * retry on its own - a rejection here means wait, not try again.
 */

/**
 * Every voucher on the session, straight from the server.
 *
 * The widget renders this rather than a list it accumulates itself: a reload loses local
 * state, and a list that disagrees with the total means vouchers the customer can see but
 * cannot remove.
 */
export type AppliedVoucher = {
  code: string;
  balanceCents: number;
};

export type ValidateVoucherResult = {
  sessionId: string;
  balanceCents: number;
  totalBalanceCents: number;
  vouchers: AppliedVoucher[];
  /**
   * The same code is sitting in another booking in progress. Informational only - the
   * voucher still applies here, and whichever order pays first is the one that redeems it.
   */
  alsoInAnotherCart?: boolean;
  payload: AckSessionPayload;
};

/** Carries the customer-facing copy the server chose; never a Service NSW message. */
export class ActiveKidsApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ActiveKidsApiError";
    this.status = status;
  }
}

const GENERIC_ERROR = "Voucher checks are temporarily unavailable. Please try again shortly.";

async function post<T>(path: string, body: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${HK_API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ActiveKidsApiError(0, GENERIC_ERROR);
  }

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const message = (data as { error?: unknown })?.error;
    throw new ActiveKidsApiError(
      response.status,
      typeof message === "string" && message ? message : GENERIC_ERROR,
    );
  }

  return data as T;
}

export function validateVoucher(input: {
  code: string;
  venueId: string;
  cartToken?: string;
  sessionId?: string;
}): Promise<ValidateVoucherResult> {
  return post<ValidateVoucherResult>("/vouchers/validate", input);
}

export function clearVoucherSession(sessionId: string): Promise<{ cleared: boolean }> {
  return post<{ cleared: boolean }>("/vouchers/session/clear", { sessionId });
}

/**
 * `cleared` means that was the last voucher and the session is gone, so the cart
 * attribute has to be emptied. Otherwise `payload` is re-signed for what remains.
 */
export type RemoveVoucherResult =
  | { cleared: true }
  | {
      cleared: false;
      sessionId: string;
      totalBalanceCents: number;
      vouchers: AppliedVoucher[];
      payload: AckSessionPayload;
    };

export function removeVoucherFromSession(
  sessionId: string,
  code: string,
): Promise<RemoveVoucherResult> {
  return post<RemoveVoucherResult>("/vouchers/session/remove", { sessionId, code });
}

/** `found: false` covers expired and already-claimed sessions as well as unknown ones. */
export type ReadSessionResult =
  | { found: false }
  | { found: true; sessionId: string; totalBalanceCents: number; vouchers: AppliedVoucher[] };

export async function readVoucherSession(sessionId: string): Promise<ReadSessionResult> {
  const url = `${HK_API_BASE_URL}/vouchers/session?sessionId=${encodeURIComponent(sessionId)}`;
  let response: Response;
  try {
    response = await fetch(url);
  } catch {
    // A session we cannot read is one we cannot safely offer to edit, and this runs on
    // mount - failing loudly here would break a screen that is otherwise usable.
    return { found: false };
  }

  if (!response.ok) return { found: false };
  return ((await response.json().catch(() => null)) as ReadSessionResult | null) ?? { found: false };
}
